import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import { InterviewKind } from '@Domain/Enums/Interviews/InterviewKind';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { reveal } from '@Guidelines/Theme/helpers/animations';
import { theme } from '@Guidelines/Theme/theme';
import PlainDate from '@Domain/PlainDate';
import useLocalInterviews from '@UI/Pages/Beneficiaries/Hooks/useLocalInterviews';
import DiscardLocalDataDialog from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewUpload/ConfirmationDialogs/DiscardLocalDataDialog';
import useLocalInterviewPort from '@UI/Providers/PortsProvider/Hooks/LocalInterviews/useLocalInterviewPort';
import useOutboxItems from '@UI/Providers/PortsProvider/Hooks/OutboxRunner/useOutboxItems';
import useOutboxPort from '@UI/Providers/PortsProvider/Hooks/OutboxRunner/useOutboxPort';
import { css } from '@emotion/react';
import { AlertTriangle } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@verclam/ui-kit';

const bannerStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	padding: 1.25rem 1.5rem;
	border-radius: ${theme.radii.large};
	background-color: ${theme.states.warning.surface};
	color: ${theme.states.warning.ink};
	font-family: ${theme.fonts.body};
`;

const headerStyle = css`
	display: flex;
	align-items: center;
	gap: 0.75rem;
`;

const titleStyle = css`
	${theme.typography.cardTitle}
	margin: 0;
	color: inherit;
`;

const textStyle = css`
	margin: 0;
	font-size: ${theme.fontSizes.large};
	color: inherit;
`;

const actionsStyle = css`
	display: flex;
	flex-wrap: wrap;
	gap: 0.75rem;

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		flex-direction: column;
	}
`;

type OrphanedTexts = {
	readonly title: string;
	readonly body: string;
	readonly recreateLabel: string;
	readonly recreationFailed: string;
};

const ORPHANED_TEXTS: Readonly<Record<InterviewKind, OrphanedTexts>> = Object.freeze({
	[InterviewKind.CONVERSATION]: {
		title: 'Attention, cet entretien a été supprimé.',
		body: "Vous avez encore ses données sur cet appareil. Voulez-vous recréer un nouvel entretien avec ces données et relancer l'envoi, ou supprimer les données de cet appareil ?",
		recreateLabel: 'Recréer un nouvel entretien',
		recreationFailed: "La recréation de l'entretien a échoué.",
	},
	[InterviewKind.NOTES]: {
		title: 'Attention, cette prise de notes a été supprimée.',
		body: "Vous avez encore ses données sur cet appareil. Voulez-vous recréer une nouvelle prise de notes avec ces données et relancer l'envoi, ou supprimer les données de cet appareil ?",
		recreateLabel: 'Recréer une nouvelle prise de notes',
		recreationFailed: 'La recréation de la prise de notes a échoué.',
	},
	[InterviewKind.TEXT]: {
		title: 'Attention, ce texte saisi a été supprimé.',
		body: "Vous avez encore ses données sur cet appareil. Voulez-vous recréer un nouveau texte saisi avec ces données et relancer l'envoi, ou supprimer les données de cet appareil ?",
		recreateLabel: 'Recréer un nouveau texte saisi',
		recreationFailed: 'La recréation du texte saisi a échoué.',
	},
});

type Props = {
	readonly subject: SubjectAddress;
	readonly outboxItemId: string;
	readonly kind: InterviewKind;
};

/**
 * The interview was deleted on the server while this device still holds its recording: the user
 * decides between recreating it from the local data and erasing that data.
 */
const OrphanedInterviewBanner: React.FC<Props> = ({ subject, outboxItemId, kind }) => {
	const texts = ORPHANED_TEXTS[kind];
	const navigate = useNavigate();
	const outboxPort = useOutboxPort();
	const localInterviewPort = useLocalInterviewPort();
	const outboxItem = useOutboxItems().find((item) => item.id === outboxItemId) ?? null;
	const localInterviewId = String(outboxItem?.context.localInterviewId ?? '');
	const localInterview = useLocalInterviews().find((interview) => interview.id === localInterviewId);
	const conductedOnDisplay = PlainDate.fromApi(localInterview?.conductedOn ?? '')?.toDisplay() ?? '';

	const [isDiscardDialogVisible, setIsDiscardDialogVisible] = React.useState(false);
	const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

	const handleRecreate = React.useCallback(() => {
		setErrorMessage(null);
		localInterviewPort
			.detachServerIdentity(localInterviewId)
			.then(() => outboxPort.restart(outboxItemId))
			.then(() => {
				navigate(`${buildSubjectPath(subject)}/local-interviews/${localInterviewId}/upload`, {
					replace: true,
				});
			})
			.catch((reason: unknown) => {
				setErrorMessage(reason instanceof Error ? reason.message : texts.recreationFailed);
			});
	}, [localInterviewPort, outboxPort, localInterviewId, outboxItemId, subject, navigate, texts]);

	const handleDiscardAccept = React.useCallback(() => {
		setErrorMessage(null);
		setIsDiscardDialogVisible(false);
		outboxPort
			.discard(outboxItemId)
			.then(() => localInterviewPort.remove(localInterviewId))
			.then(() => {
				navigate(buildSubjectPath(subject), { replace: true });
			})
			.catch((reason: unknown) => {
				setErrorMessage(reason instanceof Error ? reason.message : 'La suppression a échoué.');
			});
	}, [outboxPort, localInterviewPort, localInterviewId, outboxItemId, subject, navigate]);

	const handleOpenDiscardDialog = React.useCallback(() => setIsDiscardDialogVisible(true), []);
	const handleHideDiscardDialog = React.useCallback(() => setIsDiscardDialogVisible(false), []);

	return (
		<section css={[bannerStyle, reveal(0)]} role="alert">
			<div css={headerStyle}>
				<AlertTriangle size={22} aria-hidden="true" />
				<h2 css={titleStyle}>{texts.title}</h2>
			</div>
			<p css={textStyle}>{texts.body}</p>
			<ConditionalLoader data={errorMessage}>
				{(message) => <p css={textStyle}>{message}</p>}
			</ConditionalLoader>
			<div css={actionsStyle}>
				<Button type="button" label={texts.recreateLabel} onClick={handleRecreate} />
				<Button
					type="button"
					severity="danger"
					label="Supprimer les données de cet appareil"
					onClick={handleOpenDiscardDialog}
				/>
			</div>
			<DiscardLocalDataDialog
				visible={isDiscardDialogVisible}
				conductedOnDisplay={conductedOnDisplay}
				onHide={handleHideDiscardDialog}
				onAccept={handleDiscardAccept}
			/>
		</section>
	);
};

export default OrphanedInterviewBanner;
